import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import axios from 'axios';
import { 
  CheckCircle, Package, Truck, Home, Clock,
  BookOpen, MapPin, CreditCard, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderTracking = () => {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const orderStatuses = [
    { key: 'pending', icon: Clock, label: t('order.status.pending') },
    { key: 'paid', icon: CreditCard, label: t('order.status.paid') },
    { key: 'processing', icon: Package, label: t('order.status.processing') },
    { key: 'printing', icon: BookOpen, label: t('order.status.printing') },
    { key: 'shipped', icon: Truck, label: t('order.status.shipped') },
    { key: 'delivered', icon: Home, label: t('order.status.delivered') },
  ];

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/orders/${orderId}`, { withCredentials: true });
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  const getStatusIndex = (status) => {
    return orderStatuses.findIndex(s => s.key === status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Card className="max-w-md w-full text-center">
            <CardContent className="py-12">
              <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="font-heading text-xl font-semibold mb-2">{t('orderTracking.orderNotFound')}</h2>
              <Link to="/dashboard">
                <Button variant="outline">{t('orderTracking.backToPanel')}</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentStatusIndex = getStatusIndex(order.status);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4" />
            {t('orderTracking.backToPanel')}
          </Link>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground">
                {t('order.tracking')}
              </h1>
              <p className="text-muted-foreground">{t('orderTracking.order')} {order.order_id}</p>
            </div>
            <Badge className={`status-badge ${
              order.status === 'delivered' ? 'bg-green-100 text-green-700' :
              order.status === 'shipped' ? 'bg-indigo-100 text-indigo-700' :
              order.status === 'paid' ? 'bg-blue-100 text-blue-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
              {t(`order.status.${order.status}`)}
            </Badge>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Tracking Progress */}
            <div className="lg:col-span-2">
              <Card className="card-elevated" data-testid="tracking-progress">
                <CardHeader>
                  <CardTitle className="font-heading">{t('orderTracking.orderStatus')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {orderStatuses.map((status, index) => {
                      const isCompleted = index <= currentStatusIndex;
                      const isCurrent = index === currentStatusIndex;
                      const Icon = status.icon;

                      return (
                        <div 
                          key={status.key} 
                          className={`flex items-center gap-4 ${!isCompleted && 'opacity-40'}`}
                          data-testid={`status-${status.key}`}
                        >
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isCompleted ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'
                          } ${isCurrent ? 'ring-4 ring-primary/20' : ''}`}>
                            {isCompleted && index < currentStatusIndex ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : (
                              <Icon className="w-5 h-5" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={`font-medium ${isCompleted ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {status.label}
                            </p>
                            {isCurrent && (
                              <p className="text-sm text-muted-foreground">{t('orderTracking.inProgress')}</p>
                            )}
                          </div>
                          {index < orderStatuses.length - 1 && (
                            <div className={`absolute left-5 top-10 w-0.5 h-8 ${
                              isCompleted ? 'bg-primary' : 'bg-gray-200'
                            }`} />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Tracking Number */}
                  {order.tracking_number && (
                    <div className="mt-8 p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">{t('orderTracking.trackingNumber')}</p>
                      <p className="font-mono font-semibold">{order.tracking_number}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Order Details */}
            <div className="lg:col-span-1">
              <Card className="card-elevated" data-testid="order-details">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">{t('orderTracking.orderDetails')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Book Info */}
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-14 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium line-clamp-2">{order.book_title}</p>
                      <p className="text-sm text-muted-foreground">{order.quantity} {order.quantity === 1 ? t('orderTracking.copy') : t('orderTracking.copies')}</p>
                    </div>
                  </div>

                  <Separator />

                  {/* Shipping Address */}
                  <div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <MapPin className="w-4 h-4" />
                      {t('orderTracking.shippingAddress')}
                    </div>
                    <p className="text-sm">
                      {order.shipping_name}<br />
                      {order.shipping_address}<br />
                      {order.shipping_postal_code} {order.shipping_city}<br />
                      {order.shipping_country}
                    </p>
                  </div>

                  <Separator />

                  {/* Pricing */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{t('orderTracking.subtotal')}</span>
                      <span>{(order.unit_price * order.quantity).toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>{t('orderTracking.shippingCost')}</span>
                      <span>{order.shipping_cost.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-2 border-t">
                      <span>Total</span>
                      <span>{order.total_amount.toFixed(2)}€</span>
                    </div>
                  </div>

                  <Separator />

                  {/* Dates */}
                  <div className="text-sm text-muted-foreground">
                    <p>{t('orderTracking.orderDate')}: {new Date(order.created_at).toLocaleDateString('es', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Success page after payment
export const OrderSuccess = () => {
  const { t } = useTranslation();
  const { orderId } = useParams();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-md mx-auto text-center">
          <Card className="card-elevated" data-testid="order-success">
            <CardContent className="py-12">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold mb-2">{t('order.success')}</h1>
              <p className="text-muted-foreground mb-6">{t('order.successDesc')}</p>
              <p className="text-sm text-muted-foreground mb-6">
                {t('orderTracking.order')}: <span className="font-mono font-medium">{orderId}</span>
              </p>
              <div className="flex flex-col gap-3">
                <Link to={`/orders/${orderId}`}>
                  <Button className="w-full btn-primary">{t('order.tracking')}</Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="outline" className="w-full">{t('orderTracking.backToPanel')}</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OrderTracking;
